import { Card, CardContent, CardHeader, Divider } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { CircleLoader } from '@components/Loaders';
import RoundedButton from '@components/RoundedButton/index';
import SummaryRow from '@components/SummaryRow';

import { useAccountPlans } from '@hooks/api/account';

import type {
  OnboardingMember,
  OnboardingUser,
} from '@models/onboarding-member';

import { formatAddress, formatName } from '@utils/format';

import type { AccountValues } from './types';

const messages = defineMessages({
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  back: {
    id: 'GLOBAL.back',
    defaultMessage: 'Back',
  },
  address: {
    id: 'GLOBAL.address',
    defaultMessage: 'Address',
  },
  memberInfo: {
    id: 'adminPaymentSummary.memberInfo',
    defaultMessage: 'Member Information',
  },
  name: {
    id: 'adminPaymentSummary.name',
    defaultMessage: 'Name: ',
  },
  email: {
    id: 'adminPaymentSummary.email',
    defaultMessage: 'Email: ',
  },
  roles: {
    id: 'adminPaymentSummary.roles',
    defaultMessage: 'Roles: ',
  },
  plan: {
    id: 'adminPaymentSummary.plan',
    defaultMessage: 'Plan: ',
  },
  accountInfo: {
    id: 'adminPaymentSummary.accountInfo',
    defaultMessage: 'Account Information',
  },
});

interface AccountSummaryProps {
  members: (OnboardingUser | OnboardingMember)[];
  account: AccountValues;
  onBack: () => void;
  onSubmit: () => Promise<void>;
  isAddForm?: boolean;
  /** Optional. ID of the parent to add this account under. */
  parent_id?: string | null;
}

const displayRoles = (member: OnboardingUser | OnboardingMember) => {
  if ('roles' in member) {
    return member.roles
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(({ label }) => label)
      .join(', ');
  } else if ('role' in member) {
    return member.role;
  }
  return '-';
};

const AccountSummary = ({
  members,
  account,
  onBack,
  onSubmit,
  isAddForm = false,
  parent_id,
}: AccountSummaryProps) => {
  const [submitting, setSubmitting] = useState(false);
  const { planOptions } = useAccountPlans(parent_id);

  const loading = submitting;

  const { formatMessage } = useIntl();

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await onSubmit();
    } catch {
      // Both callers report their own failures before returning, so there is
      // nothing to surface here. Swallowing rather than re-throwing keeps a
      // rejection from escaping this click handler unhandled and leaving the
      // button stuck spinning.
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {!isAddForm && (
        <Card>
          <CardHeader title={formatMessage(messages.accountInfo)} />
          <Divider />
          <CardContent>
            <SummaryRow
              bottomText={account.name}
              topText={formatMessage(messages.name)}
            />

            <SummaryRow
              bottomText={formatAddress(
                [account.line1, account.line2 ?? null],
                account.city,
                account.postalCode,
                account.province,
                account.country
              )}
              topText={formatMessage(messages.address)}
            />
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader title={formatMessage(messages.memberInfo)} />
        {members.map((member, index) => {
          const plan =
            'planId' in member
              ? planOptions.find(({ k }) => k === member.planId)
              : undefined;
          return (
            <React.Fragment key={index}>
              <Divider />
              <CardContent>
                <SummaryRow
                  bottomText={formatName(
                    member.firstName,
                    member.middleName,
                    member.lastName
                  )}
                  topText={formatMessage(messages.name)}
                />
                <SummaryRow
                  bottomText={member.email}
                  topText={formatMessage(messages.email)}
                />
                <SummaryRow
                  bottomText={displayRoles(member)}
                  topText={formatMessage(messages.roles)}
                />
                {plan ? (
                  <SummaryRow
                    bottomText={plan.v}
                    topText={formatMessage(messages.plan)}
                  />
                ) : null}
              </CardContent>
            </React.Fragment>
          );
        })}
      </Card>

      <Card>
        <CardContent>
          <CircleLoader loading={loading}>
            <RoundedButton
              disabled={loading}
              onClick={handleSubmit}
              type="button"
            >
              {formatMessage(messages.submit)}
            </RoundedButton>

            <RoundedButton disabled={loading} onClick={onBack} type="button">
              {formatMessage(messages.back)}
            </RoundedButton>
          </CircleLoader>
        </CardContent>
      </Card>
    </>
  );
};

export default AccountSummary;
